

import { useParentPosition } from "@/hooks/useParentPosition";
import { useState, useEffect, useRef } from "react";
import { PInfo } from "../PInfo/PInfo";
import { IconCopy, IconCheck, IconX } from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";
import { HelpHint } from "../HelpHint/HelpHint";

type FormInputProps = {
  id: string;
  border?: string;
  rounded?: string;
  margin?: string;
  height?: string;
  label?: string;
  fontSize?: string;
  icon?: React.ElementType;
  iconSize?: number;
  iconPaddingLeft?: string;
  iconPaddingRight?: string;
  trailingContent?: React.ReactNode;
  value?: string;
  onChangeAction: (value: string) => void;
  type?:
    | "text"
    | "email"
    | "password"
    | "number"
    | "date"
    | "datetime-local"
    | "time"
    | "url"
    // A filter box. The native type is load-bearing: a host dialog's Escape
    // rule reads `type === "search"` to let the first Escape empty the box
    // (ActivityDialog). The UA's own clear button is hidden below — the
    // field's `trailingContent` / `deletion` is the one clear affordance.
    | "search";
  placeholder?: string;
  error?: string;
  className?: string;
  width?: string;
  ref?: React.Ref<HTMLInputElement | null>;
  disabled?: boolean;
  opacityWhenDisabled?: boolean;
  autoComplete?: boolean | string;
  // Extra attributes spread onto the <input> (role/aria/data-*) — used e.g. to
  // make a field a combobox and suppress browser/password-manager autofill.
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> &
    Record<`data-${string}`, string | boolean>;
  surface?: string;
  paddingInput?: string;
  onBlurAction?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onEnterAction?: () => void;
  onFocusAction?: () => void;
  hoverMessage?: string;
  // Delayed "what is this?" hint rendered as an info icon next to the label.
  // Distinct from `hoverMessage` (whole-label instant tooltip): this appears
  // only after the pointer rests on the icon, matching the guest-detail popup.
  info?: string;
  maxLength?: number;
  labelFontSize?: string;
  deletion?: boolean;
  copy?: boolean;
  /** Native HTML5 min attr — forwarded to the underlying `<input>`. Only
   *  meaningful for `type="number" | "date" | "datetime-local"`. Does not
   *  block typing; it constrains stepper buttons + browser validation hint.
   *  Explicit error strings (via the `error` prop) remain the user-facing
   *  source of truth. */
  min?: number | string;
  /** Native HTML5 max attr — see {@link min}. */
  max?: number | string;
  /** Marks this field as the view's search box, so the global Cmd/Ctrl+F
   *  shortcut can focus it — see `lib/a11y/useSearchHotkey`. Set it on filter
   *  inputs only; a "name this template" field must NOT claim the shortcut. */
  search?: boolean;
  "data-testid"?: string;
};

export const FormInput: React.FC<FormInputProps> = ({
  id,
  deletion = false,
  surface = "",
  border = "outline-secondary border focus-within:outline-focus",
  rounded = "rounded-[10px]",
  margin = "mt-2",
  height = "h-9",
  label,
  fontSize = "body-2-regular",
  icon: Icon,
  iconSize = 20,
  iconPaddingLeft = "pl-3",
  iconPaddingRight = "pl-3",
  trailingContent,
  value = "",
  onChangeAction,
  type = "text",
  placeholder = "",
  error,
  className = "",
  width = "",
  ref,
  disabled = false,
  opacityWhenDisabled = true,
  autoComplete = false,
  inputProps,
  paddingInput = "px-2",
  onBlurAction,
  onEnterAction,
  onFocusAction,
  hoverMessage,
  info,
  maxLength,
  min,
  max,
  labelFontSize = "body-2-medium",
  copy = false,
  search = false,
  "data-testid": dataTestId,
}) => {
  const t = useTranslations("inputColumnComponent");
  // 1. Identify State & Refs
  const [showTooltip, setShowTooltip] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const labelRef = useRef<HTMLLabelElement>(null);
  const { dropdownPosition, setDropdownPosition } = useParentPosition();

  // --- START: ROBUST AUTO-TRACKING IMPLEMENTATION ---

  // 2. Add "Live Tracking" Effect (Scroll & Resize)
  useEffect(() => {
    if (showTooltip && labelRef.current) {
      const updatePosition = () => {
        if (labelRef.current) setDropdownPosition(labelRef);
      };

      // Capture phase is required for nested scrolling
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [showTooltip, setDropdownPosition]);

  // 3. Add "Immediate Failsafe" Effect
  useEffect(() => {
    if (showTooltip && labelRef.current) {
      // Force calculation immediately when state becomes true
      // This prevents visual flickering at (0,0)
      setDropdownPosition(labelRef);
    }
  }, [showTooltip, setDropdownPosition]);

  // 4. Centralize "Open" Logic
  const handleMouseEnter = () => {
    if (labelRef.current) {
      // Order of Operations:
      // 1. Calculate position
      setDropdownPosition(labelRef);
      // 2. Then show component
      setShowTooltip(true);
    }
  };

  // --- END: ROBUST AUTO-TRACKING IMPLEMENTATION ---

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled && !copy) {
      onChangeAction(e.target.value);
    }
  };

  const handleCopy = () => {
    if (value) {
      navigator.clipboard
        .writeText(value)
        .then(() => {
          setCopySuccess(true);
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
        });
    }
  };

  useEffect(() => {
    if (copySuccess) {
      const timer = setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copySuccess]);

  return (
    <div className={`flex flex-col ${className} ${width} relative`}>
      {label && (
        <HelpHint message={info}>
          <label
            ref={labelRef}
            onMouseEnter={handleMouseEnter} // Use centralized handler
            onMouseLeave={() => setShowTooltip(false)}
            htmlFor={id}
            className={`font-secondary w-max max-w-full ${labelFontSize} ${
              opacityWhenDisabled && disabled ? "opacity-50" : ""
            } ${hoverMessage ? "cursor-help" : ""} `}
          >
            {label}
          </label>
        </HelpHint>
      )}
      {/* The wrapper is the visual pill (border + rounding), so the surface
          must live HERE — painting it on the <input> only leaves the icon /
          trailingContent zones transparent, which bleeds the page background
          through on non-white pages (e.g. the create-event wizard). */}
      <div
        className={`flex flex-row justify-center items-center ${border} ${rounded} ${margin} ${height}
               ${Icon ? iconPaddingLeft : ""} ${
          opacityWhenDisabled && disabled ? "opacity-50" : ""
        } outline-quaternary ${surface || "surface-primary"}`}
      >
        {Icon && (
          <Icon
            size={iconSize}
            className={`font-tertiary ${
              opacityWhenDisabled && disabled ? "opacity-50" : ""
            }`}
          />
        )}
        <input
          {...inputProps}
          maxLength={maxLength}
          min={min}
          max={max}
          ref={ref}
          id={id}
          name={id}
          // The error text is a sibling `role="alert"`: it announces ONCE when
          // it appears, and a user who tabs back into the field afterwards
          // would otherwise hear the value with no hint that it was rejected.
          // It matters here because refused saves focus this input
          // programmatically (ProgramItemForm's firstGapFieldId).
          // A caller that validates on its own (no `error` prop, its own
          // error line) may still mark the box invalid through `inputProps`;
          // the spread above sits before this attribute, so honour it here.
          aria-invalid={error ? true : inputProps?.["aria-invalid"]}
          aria-describedby={
            error && id
              ? [inputProps?.["aria-describedby"], `${id}-error`]
                  .filter(Boolean)
                  .join(" ")
              : inputProps?.["aria-describedby"]
          }
          type={type}
          data-search-input={search ? "true" : undefined}
          value={value}
          data-testid={dataTestId}
          onChange={handleChange}
          onFocus={(e) => {
            if (copy) {
              e.preventDefault();
              e.target.blur();
            } else if (onFocusAction) {
              onFocusAction();
            }
          }}
          onBlur={(e) => onBlurAction && !copy && onBlurAction(e)}
          onKeyDown={(e) => {
            // Compose rather than replace: this handler is declared AFTER the
            // {...inputProps} spread above, so it silently shadowed any
            // onKeyDown passed through that escape hatch — which is how a
            // combobox consumer (TemplateVariableList) loses its arrow keys.
            inputProps?.onKeyDown?.(e);
            if (e.defaultPrevented) return;
            if (e.key === "Enter" && !e.nativeEvent.isComposing && !copy) {
              e.preventDefault();
              if (onEnterAction) onEnterAction();
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={copy}
          className={`bg-transparent ${
            trailingContent || copy ? iconPaddingRight : ""
          } ${paddingInput} h-full w-full items-center ${rounded} focus:outline-hidden placeholder:font-tertiary placeholder:${fontSize} ${fontSize} ${
            (opacityWhenDisabled && disabled) || copy ? "opacity-50" : ""
          } ${copy ? "cursor-default select-all" : ""} ${
            type === "search"
              ? "[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
              : ""
          } ${
            // The focused day / month / hour segment in house tokens, not the
            // OS highlight (blue) — see `@utility datetime-segments`.
            type === "date" || type === "time" || type === "datetime-local"
              ? "datetime-segments"
              : ""
          }`}
          autoComplete={
            typeof autoComplete === "string"
              ? autoComplete
              : autoComplete === false
                ? "off"
                : "on"
          }
        />

        {trailingContent && (
          <div className="flex items-center">{trailingContent}</div>
        )}

        {!trailingContent && deletion && (
          <button
            type="button"
            onClick={() => onChangeAction("")}
            aria-label={t("clearInput")}
            className={`focus:outline-hidden hover:text-primary transition-colors mr-2 ${
              opacityWhenDisabled && disabled
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
            disabled={disabled}
          >
            <IconX size={iconSize} className="font-tertiary" />
          </button>
        )}

        {!trailingContent && copy && (
          <div className="pr-3 flex items-center">
            <button
              type="button"
              onClick={handleCopy}
              className="focus:outline-hidden hover:text-primary transition-colors"
              aria-label={t("copyText")}
            >
              <AnimatePresence mode="wait" initial={false}>
                {copySuccess ? (
                  <motion.span
                    key="check"
                    className="flex"
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.5, opacity: 0 }}
                    transition={{ duration: 0.08 }}
                  >
                    <IconCheck size={iconSize} className="font-success" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="copy"
                    className="flex"
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.5, opacity: 0 }}
                    transition={{ duration: 0.08 }}
                  >
                    <IconCopy size={iconSize} className="font-tertiary" />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        )}
      </div>

      {error && (
        <p
          role="alert"
          id={id ? `${id}-error` : undefined}
          className="mt-1 body-3-regular font-error"
        >
          {error}
        </p>
      )}

      {/* PInfo / Tooltip Dropdown */}
      {showTooltip && hoverMessage && (
        <PInfo message={hoverMessage} position={dropdownPosition} />
      )}
    </div>
  );
};
